import { derivedAsync } from 'ngxtension/derived-async';
import { Observable, Subject, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, filter, map, switchMap, tap } from 'rxjs/operators';

import { ChangeDetectionStrategy, Component, forwardRef, input, output, signal } from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';

import { ApmTranslatePipe } from '@mitsubishi/core';
import { SelectComponent } from '@mitsubishi/shared/ui';

import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'apm-autocomplete',
  templateUrl: './autocomplete.component.html',
  imports: [NgSelectModule, ApmTranslatePipe, FormsModule, ApmTranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true,
    },
  ],
})
export class AutocompleteComponent extends SelectComponent {
  readonly minLengthTerm = input<number>(2);
  readonly initialValues = input<any[]>();
  override readonly searchFn = input.required<(term: string) => Observable<any[]>>();

  /**
   * @returns boolean, should return `true` if the objects are equal, `false` otherwise
   */
  readonly comparatorFn = input<(obj1: any, obj2: any) => boolean>();

  selected = output<any>();

  readonly resultsLoading = signal(false);
  searchInput$ = new Subject<string>();

  results = derivedAsync(() => {
    return this.searchInput$.pipe(
      filter(res => res !== null && res.length >= this.minLengthTerm()),
      distinctUntilChanged(),
      debounceTime(200),
      tap(() => this.resultsLoading.set(true)),
      switchMap(term => {
        return this.searchFn()(term).pipe(
          catchError(() => of([])), // empty list on error
          tap(() => this.resultsLoading.set(false))
        );
      }),
      map(results => {
        const _comparatorFn = this.comparatorFn();

        if (_comparatorFn !== undefined) {
          return results.filter(result => !this.selectedValue()?.some(selected => _comparatorFn(result, selected)));
        }

        const bindValue = this.bindValue();
        if (bindValue) {
          return results.filter(
            result => !this.selectedValue()?.some(selected => result[bindValue] === selected[bindValue])
          );
        }

        return results.filter(
          result => !this.selectedValue()?.some(selected => JSON.stringify(result) === JSON.stringify(selected))
        );
      })
    );
  });
}
